import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { formatDateToMMDDYYYY } from '@utils/dates/dateMethods';
import DatePickerInputMask from './DatePickerInputMask';

const DatePickerStyled = styled.div`
    .date-picker-icon {
        height: calc(100% - 2px);
        background-color: #eeeef4;
        width: 40px;
        border-radius: 0 5px 5px 0;
        padding: 10px;
        position: absolute;
        top: 1px;
        right: 1px;
        bottom: 1px;
    }
`;

const DatePickerContainer = styled.div`
    .react-datepicker-wrapper {
        margin-bottom: 0px !important;
    }
`;

export interface IDatePickerProps {
    id: string;
    name: string;
    onDateChange: (date: Date | null) => void;
    onDateError?: (date: string) => void;
    onFieldBlur?: (date: Date | null) => void;
    minDate?: Date | undefined;
    maxDate?: Date | undefined;
    placeholder?: string;
    dateFormat?: string;
    value?: Date | undefined;
    showYearDropdown?: boolean;
    showIcon?: boolean;
    noOfYearsToShow?: number;
    [x: string]: any;
    hasError?: boolean;
    withMask?: boolean;
    isRequired: boolean;
    labelText: string;
    disabled?: boolean;
    datePickerClass?: string;
}

const ReactDatepicker = forwardRef<HTMLDivElement, IDatePickerProps>(
    (
        {
            id,
            name,
            onDateChange,
            onFieldBlur,
            minDate = undefined,
            maxDate = undefined,
            placeholder = 'MM/DD/YYYY',
            dateFormat = 'MM/dd/yyyy',
            value,
            showYearDropdown = true,
            showIcon = true,
            noOfYearsToShow = 100,
            hasError = false,
            withMask = false,
            isRequired,
            labelText,
            disabled,
            datePickerClass = 'gi-input',
            onDateError,
            onResetDateError,
            ...rest
        },
        ref,
    ) => {
        const commonProps = {
            autoComplete: 'off',
            dateFormat,
            icon: showIcon ? <></> : <FontAwesomeIcon icon="calendar-alt" />,
            name: name,
            placeholderText: placeholder,
            scrollableYearDropdown: true,
            showIcon,
            showYearDropdown,
            yearDropdownItemNumber: noOfYearsToShow,
            selected: value || rest.selected || undefined,
            portalId: 'root-portal',
        };

        const handleValueBlur = () => {
            onFieldBlur && onFieldBlur(value);
        };

        return (
            <>
                <DatePickerContainer className={datePickerClass}>
                    {labelText && (
                        <label
                            htmlFor={id}
                            className={`gi-input__label required-${isRequired}`}
                            data-testid={`${id}-text-label`}
                        >
                            <FormattedMessage id={labelText} />
                        </label>
                    )}
                    {withMask ? (
                        <DatePicker
                            {...rest}
                            {...commonProps}
                            selected={value}
                            onChange={date => {
                                onDateChange(date!);
                            }}
                            onBlur={handleValueBlur}
                            maxDate={maxDate}
                            minDate={minDate}
                            disabled={disabled}
                            customInput={
                                <DatePickerStyled>
                                    <DatePickerInputMask
                                        value={formatDateToMMDDYYYY(value)}
                                        id={id}
                                        disabled={disabled}
                                        placeholder={placeholder}
                                        maxDate={maxDate}
                                        minDate={minDate}
                                        isRequired={isRequired}
                                        // Adding this event as invalid date event
                                        // is not being handled in the DatePickerInputMask
                                        onDateError={onDateError}
                                        onResetDateError={onResetDateError}
                                    />
                                    {showIcon && <FontAwesomeIcon icon="calendar-alt" className="date-picker-icon" aria-hidden="true" />}
                                </DatePickerStyled>
                            }
                        />
                    ) : (
                        <DatePicker
                            {...rest}
                            {...commonProps}
                            id={id}
                            required={isRequired}
                            selected={value}
                            maxDate={maxDate}
                            minDate={minDate}
                            onChange={e => onDateChange(e!)}
                        />
                    )}
                </DatePickerContainer>
            </>
        );
    },
);

ReactDatepicker.displayName = 'ReactDatepicker';

export default ReactDatepicker;
